<template>
    <Form v-slot="{ validate }" as="div">
        <div class="flex items-center justify-between mt-2 mb-6 md:mt-8 md:mb-20 print:hidden">
            <button
                v-if="participant.status !== 'REGISTERED_COMPLETE'"
                type="button"
                class="flex items-center cursor-pointer w-fit"
                @click="previousStep">
                <Icon name="chevron-left" class="w-3 h-3 text-black-300 stroke-current" />
                <span class="text-black-600 text-para-xs ml-4"> {{ labels.backTo }} {{ labels.participation }} </span>
            </button>
            <button type="button" class="hidden md:flex items-center group cursor-pointer" @click="toOverview">
                <span class="text-para-s font-semibold">{{ labels.personalOverview }}</span>
                <ArrowButton direction="person" class="w-10 h-10" />
            </button>
        </div>
        <h2 class="text-heading-2 md:text-heading-1">
            {{ labels.travelDetails }}
        </h2>
        <p
            v-if="participant.status !== 'REGISTERED_COMPLETE'"
            class="text-para-s md:text-para-xl mt-4 md:mt-2"
            v-html="labels.travelDetailsDescription" />
        <p v-else class="text-para-s md:text-para-xl mt-4 md:mt-2" v-html="labels.travelDetailsDescriptionEdit" />
        <div class="mt-10 md:mt-14">
            <label>
                <span class="font-semibold text-para-xs md:text-para-s">
                    {{ labels.meansOFTravel }}
                    <dfn title="required">*</dfn>
                </span>
            </label>
            <ToggleButtons
                v-model="meansOfTravelModel"
                :items="{
                    CAR: labels.car,
                    TRAIN: labels.train,
                    AIRPLANE: labels.airplane,
                }"
                :views="{ TRAIN: 'train', AIRPLANE: 'airplane' }"
                empty-value="UNKNOWN"
                grow
                col-mobile
                class="mt-4">
                <template #train>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                        <VueFormSelection
                            v-model="participant.shuttleNeeded"
                            name="shuttleNeeded"
                            type="select"
                            :required="true"
                            :title="labels.transportationNeeded"
                            :placeholder="labels.pleaseChoose"
                            :description="labels.transportationNeededTrainHint"
                            :labels="[labels.yes + ':true', labels.no + ':false']"
                            @update:modelValue="onFocusChange(true, 'shuttleNeeded', participant.shuttleNeeded)" />
                        <template v-if="participant.shuttleNeeded">
                            <VueFormInput
                                v-model="arrivalDateModel"
                                name="arrivalDate"
                                :title="labels.arrivalDate"
                                type="date"
                                class="col-start-1"
                                :rules="{
                                    betweenDates: [
                                        event.startDate,
                                        event.endDate2,
                                    ],
                                    beforeDate: [
                                        participant.departureTime,
                                        'departure',
                                    ],
                                }"
                                @blur="onFocusChange($event, 'arrivalTime', participant.arrivalTime)" />
                            <VueFormInput
                                v-model="arrivalTimeModel"
                                name="arrivalTime"
                                :title="labels.arrivalTime"
                                type="time"
                                @blur="onFocusChange($event, 'arrivalTime', participant.arrivalTime)" />
                            <VueFormInput
                                v-model="departureDateModel"
                                name="departureDate"
                                :title="labels.departureDate"
                                type="date"
                                class="col-start-1"
                                :rules="{
                                    betweenDates: [
                                        event.startDate,
                                        event.endDate2,
                                    ],
                                }"
                                @blur="onFocusChange($event, 'departureTime', participant.departureTime)" />
                            <VueFormInput
                                v-model="departureTimeModel"
                                name="departureTime"
                                :title="labels.departureTime"
                                type="time"
                                @blur="onFocusChange($event, 'departureTime', participant.departureTime)" />
                        </template>
                    </div>
                </template>
                <template #airplane>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                        <VueFormSelection
                            v-model="participant.shuttleNeeded"
                            name="shuttleNeeded"
                            type="select"
                            :required="true"
                            :title="labels.transportationNeeded"
                            :placeholder="labels.pleaseChoose"
                            :description="labels.transportationNeededPlaneHint"
                            :labels="[labels.yes + ':true', labels.no + ':false']"
                            @update:modelValue="onFocusChange(true, 'shuttleNeeded', participant.shuttleNeeded)" />
                        <template v-if="participant.shuttleNeeded">
                            <VueFormInput
                                v-model="participant.arrivalFlightNo"
                                name="arrivalFlightNo"
                                :title="labels.arrivalFlightNo"
                                class="col-start-1"
                                @blur="onFocusChange($event, 'arrivalFlightNo', participant.arrivalFlightNo)" />
                            <VueFormInput
                                v-model="participant.arrivalFlightFrom"
                                name="arrivalFlightFrom"
                                :title="labels.arrivalFlightFrom"
                                @blur="onFocusChange($event, 'arrivalFlightFrom', participant.arrivalFlightFrom)" />
                            <VueFormInput
                                v-model="arrivalDateModel"
                                name="arrivalDate"
                                :title="labels.arrivalDate"
                                type="date"
                                :rules="{
                                    betweenDates: [
                                        event.startDate,
                                        event.endDate2,
                                    ],
                                    beforeDate: [
                                        participant.departureTime,
                                        'departure',
                                    ],
                                }"
                                @blur="onFocusChange($event, 'arrivalTime', participant.arrivalTime)" />
                            <VueFormInput
                                v-model="arrivalTimeModel"
                                name="arrivalTime"
                                :title="labels.arrivalTime"
                                type="time"
                                @blur="onFocusChange($event, 'arrivalTime', participant.arrivalTime)" />
                            <VueFormInput
                                v-model="participant.departureFlightNo"
                                name="departureFlightNo"
                                :title="labels.departureFlightNo"
                                class="col-start-1"
                                @blur="onFocusChange($event, 'departureFlightNo', participant.departureFlightNo)" />
                            <VueFormInput
                                v-model="participant.departureFlightTo"
                                name="departureFlightTo"
                                :title="labels.departureFlightTo"
                                @blur="onFocusChange($event, 'departureFlightTo', participant.departureFlightTo)" />
                            <VueFormInput
                                v-model="departureDateModel"
                                name="departureDate"
                                :title="labels.departureDate"
                                type="date"
                                class="col-start-1"
                                :rules="{
                                    betweenDates: [
                                        event.startDate,
                                        event.endDate2,
                                    ],
                                }"
                                @blur="onFocusChange($event, 'departureTime', participant.departureTime)" />
                            <VueFormInput
                                v-model="departureTimeModel"
                                name="departureTime"
                                :title="labels.departureTime"
                                type="time"
                                @blur="onFocusChange($event, 'departureTime', participant.departureTime)" />
                        </template>
                    </div>
                </template>
            </ToggleButtons>
            <span v-if="showRequired" class="text-error text-para-xs mt-1 md:mt-2">
                {{ labels.travelRequired }}
            </span>
        </div>
        <div class="font-black mt-20 mb-4">{{ labels.contactDetails }}:</div>
        <div>{{ event.paName }}</div>
        <div>{{ labels.mail }}: {{ event.paMail }}</div>
        <div>{{ labels.phone }}: {{ event.paPhone }}</div>
        <div>{{ labels.mobile }}: {{ event.paMobile }}</div>
        <p class="text-para-s md:text-para-base mt-10 md:mt-16" v-html="labels.requiredHint" />
        <template v-if="participant.status !== 'REGISTERED_COMPLETE'">
            <div class="flex justify-end">
                <button
                    class="flex flex-row-reverse items-center group w-full mt-8 md:mt-12 max-w-max"
                    @click="onSubmit(validate)">
                    <ArrowButton class="w-10 h-10 flex-shrink-0" show-ring-on-mobile />
                    <span class="font-semibold text-para-s line-clamp-1">{{ labels.next }}</span>
                </button>
            </div>
            <div class="md:hidden border-t border-black-200 mt-12">
                <button type="button" class="flex items-center group cursor-pointer mt-6" @click="toOverview">
                    <span class="text-para-s font-semibold">{{ labels.personalOverview }}</span>
                    <ArrowButton direction="person" class="w-10 h-10" show-ring-on-mobile />
                </button>
            </div>
        </template>
        <template v-else>
            <div class="flex justify-end">
                <button
                    class="flex flex-row-reverse items-center group w-full mt-8 md:mt-12"
                    @click="onUpdateTravelData(validate)">
                    <ArrowButton class="w-10 h-10 flex-shrink-0" show-ring-on-mobile />
                    <span class="font-semibold text-para-s line-clamp-1">{{ labels.bookingUpdate }}</span>
                </button>
            </div>
            <div class="md:hidden border-t border-black-200 mt-12">
                <button type="button" class="flex items-center group cursor-pointer mt-6" @click="toOverview">
                    <span class="text-para-s font-semibold">{{ labels.personalOverview }}</span>
                    <ArrowButton direction="person" class="w-10 h-10" show-ring-on-mobile />
                </button>
            </div>
        </template>
    </Form>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { GolfEventLabels } from './GolfEventTypes';
import Icon from '../base/Icon.vue';
import ArrowButton from '../base/ArrowButton.vue';
import ToggleButtons from '../base/ToggleButtons.vue';
import VueFormSelection from '../form/VueFormSelection.vue';
import VueFormInput from '../form/VueFormInput.vue';
import { Form, FormValidationResult, GenericObject } from 'vee-validate';

export default {
    // eslint-disable-next-line vue/no-reserved-component-names
    components: {
        Form,
        VueFormInput,
        VueFormSelection,
        ToggleButtons,
        ArrowButton,
        Icon,
    },
    inject: [
        'participant',
        'event',
        'updateData',
        'updateProperty',
        'nextStep',
        'previousStep',
        'toOverview',
        'confirmBooking',
    ],
    props: {
        labels: { type: Object as PropType<GolfEventLabels>, required: true },
    },
    data() {
        return {
            dataModel: [],
            showRequired: false,
        };
    },
    computed: {
        meansOfTravelModel: {
            get() {
                return [this.participant.meansOfTravel];
            },
            set(newVal: string[]) {
                if (newVal.length) {
                    this.participant['meansOfTravel'] = newVal[0];
                    if (newVal[0] !== 'UNKNOWN') {
                        this.showRequired = false;
                    }
                }
                this.updateProperty('meansOfTravel', this.participant.meansOfTravel);
            },
        },
        arrivalDateModel: {
            get() {
                if (!this.participant.arrivalTime) return undefined;
                return this.getSwissDateString(new Date(this.participant.arrivalTime));
            },
            set(newVal: string) {
                // use previously entered time, if it exists, 00:00 otherwise
                const timeStr = this.participant.arrivalTime
                    ? this.getSwissTimeString(new Date(this.participant.arrivalTime))
                    : '00:00';
                const date = this.getDateInSwissTimezone(newVal, timeStr);
                if (!isNaN(date.getTime())) {
                    this.participant['arrivalTime'] = date.getTime();
                }
            },
        },
        arrivalTimeModel: {
            get() {
                if (!this.participant.arrivalTime) return undefined;
                return this.getSwissTimeString(new Date(this.participant.arrivalTime));
            },
            set(newVal: string) {
                // use previously entered date, if it exists, current date otherwise
                const dateStr = this.participant.arrivalTime
                    ? this.getSwissDateString(new Date(this.participant.arrivalTime))
                    : this.getSwissDateString(new Date());
                const date = this.getDateInSwissTimezone(dateStr, newVal);
                if (!isNaN(date.getTime())) {
                    this.participant['arrivalTime'] = date.getTime();
                }
            },
        },
        departureDateModel: {
            get() {
                if (!this.participant.departureTime) return undefined;
                return this.getSwissDateString(new Date(this.participant.departureTime));
            },
            set(newVal: string) {
                // use previously entered time, if it exists, 00:00 otherwise
                const timeStr = this.participant.departureTime
                    ? this.getSwissTimeString(new Date(this.participant.departureTime))
                    : '00:00';
                const date = this.getDateInSwissTimezone(newVal, timeStr);
                if (!isNaN(date.getTime())) {
                    this.participant['departureTime'] = date.getTime();
                }
            },
        },
        departureTimeModel: {
            get() {
                if (!this.participant.departureTime) return undefined;
                return this.getSwissTimeString(new Date(this.participant.departureTime));
            },
            set(newVal: string) {
                // use previously entered date, if it exists, current date otherwise
                const dateStr = this.participant.departureTime
                    ? this.getSwissDateString(new Date(this.participant.departureTime))
                    : this.getSwissDateString(new Date());
                const date = this.getDateInSwissTimezone(dateStr, newVal);
                if (!isNaN(date.getTime())) {
                    this.participant['departureTime'] = date.getTime();
                }
            },
        },
    },
    created(): void {
        // this should preset the 'now' choice once a user selected at least the means of travel
        if (this.participant.meansOfTravel) {
            this.dataModel = ['now'];
        }
    },
    mounted(): void {
        this.$el.scrollIntoView({ behavior: 'smooth' });
    },
    methods: {
        onFocusChange(passed: boolean, name: string, value: any): void {
            if (passed) {
                this.updateProperty(name, value);
            }
        },
        onSubmit(validate: () => Promise<FormValidationResult<GenericObject, GenericObject>>): void {
            if (!(this.participant.meansOfTravel && this.participant.meansOfTravel !== 'UNKNOWN')) {
                this.showRequired = true;
                return;
            }
            this.showRequired = false;

            validate().then(result => {
                if (result.valid) {
                    this.nextStep();
                }
            });
        },
        onUpdateTravelData(validate: () => Promise<FormValidationResult<GenericObject, GenericObject>>): void {
            validate().then(result => {
                if (result.valid) {
                    // submits and shows summary
                    this.nextStep();
                }
            });
        },
        getSwissDateString(date: Date): string {
            // add two hours to get to Swiss summer timezone
            const swissDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
            return swissDate.toISOString().split('T')[0];
        },
        getSwissTimeString(date: Date): string {
            // add two hours to get to Swiss summer timezone
            const swissDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
            return swissDate.toISOString().split('T')[1].slice(0, 5);
        },
        getDateInSwissTimezone(dateStr: string, timeStr: string): Date {
            return new Date(`${dateStr}T${timeStr}:00.000+02:00`); // in Swiss summer timezone
        },
    },
};
</script>
